import type { CSSProperties } from 'react';

import { DateUtils } from '@spotify/confidence-core-react';
import { AttachmentFragment } from '@spotify/confidence-graphql';
import { projectsPlugin } from '@spotify/plugin-confidence-projects';
import {
  ExternalUrlArtifactContent,
  ExternalUrlArtifactListItem,
  createArtifactExtension,
  getCodingSessionRef,
  getCodingSessionUrl,
} from '@spotify/plugin-confidence-projects-react';

const HONK_PROVIDER = 'honk';

const ChirpIcon = ({
  size = 16,
  className,
  style,
}: {
  size?: number | string;
  className?: string;
  style?: CSSProperties;
}) => (
  <svg
    aria-hidden="true"
    className={className}
    fill="none"
    height={size}
    stroke="var(--text-warning)"
    strokeLinecap="round"
    strokeLinejoin="round"
    strokeWidth="2"
    style={style}
    viewBox="0 0 24 24"
    width={size}
  >
    <path d="M16 7h.01" />
    <path d="M3.4 18H12a8 8 0 0 0 8-8V7a4 4 0 0 0-7.28-2.3L2 20" />
    <path d="m20 7 2 .5-2 .5" />
    <path d="M10 18v3" />
    <path d="M14 17.75V21" />
    <path d="M7 18a6 6 0 0 0 3.84-10.61" />
  </svg>
);

const getLegacyChirpSessionId = (uri: string): string | undefined => {
  try {
    const { hostname, pathname } = new URL(uri);
    const match = pathname.match(/^\/chirp\/sessions\/([^/]+)$/);
    if (hostname === 'backstage.spotify.net' && match) {
      return match[1];
    }
  } catch {
    return undefined;
  }

  return undefined;
};

const getChirpSessionId = (
  artifact: AttachmentFragment,
): string | undefined => {
  const session = getCodingSessionRef(artifact.uri);
  if (session?.provider === HONK_PROVIDER) {
    return session.sessionId;
  }
  return getLegacyChirpSessionId(artifact.uri);
};

const getChirpHref = (artifact: AttachmentFragment): string | undefined => {
  if (getLegacyChirpSessionId(artifact.uri)) {
    return artifact.uri;
  }
  const session = getCodingSessionRef(artifact.uri);
  return session?.provider === HONK_PROVIDER
    ? getCodingSessionUrl(artifact)
    : undefined;
};

const isChirpCodingSession = (artifact: AttachmentFragment): boolean =>
  getChirpSessionId(artifact) !== undefined;

export const ChirpCodingSessionArtifactExtension = projectsPlugin.provide(
  createArtifactExtension({
    id: 'chirp-coding-session',
    predicate: isChirpCodingSession,
    icon: () => ({
      Icon: ChirpIcon,
      color: 'var(--essential-warning)',
    }),
    label: 'Chirp coding session',
    category: 'Coding sessions',
    render: artifact => (
      <ExternalUrlArtifactContent
        artifact={artifact}
        href={getChirpHref(artifact)}
      />
    ),
    renderListItem: (artifact, { onDelete, onRename, icon }) => {
      const href = getChirpHref(artifact);
      return (
        <ExternalUrlArtifactListItem
          artifact={artifact}
          icon={icon}
          subtitleLabel="Chirp coding session"
          openLabel="Open in Chirp"
          href={href}
          createTimeFormat={DateUtils.SHORT_MONTH_DAY_TIME_FORMAT}
          onDelete={onDelete}
          onRename={onRename}
        />
      );
    },
  }),
);
